import * as React from 'react';
import { Virtuoso } from 'react-virtuoso';
import { InfiniteListItem } from './infinite_list_item.js';
import { InfiniteListLoadingFooter } from './infinite_list_loading_footer.js';
import '../list/index.js';
import { List } from '../list/list.js';

/**
 * Wrapper we need to avoid re-rendering unnecessarily
 * Also handles the mis-typing of react-virtuoso by doing a cast of the ref type
 */
const InfiniteListWrapper = React.forwardRef(function InfiniteListWrapper({ children, context, ...rest }, ref) {
    return (React.createElement(List, { gap: context.gap, ...rest, as: "ul", ref: ref }, children));
});
/**
 * Automatically sized, virtualized vertical infinite list that supports dynamic row height
 */
const InfiniteList = React.forwardRef(function InfiniteList({ data, itemContent, itemKey, fixedItemHeight, estimatedItemHeight, loadMoreData, overscan, className, components: propComponents, initialItemCount, useWindowScroll, scrollParent, context, stickyItemCount, gap = 'medium', ...rest }, ref) {
    const renderItemContent = React.useCallback((index, item, context) => {
        return React.createElement(itemContent, { item, index, context });
    }, [itemContent]);
    const components = React.useMemo(() => ({
        Header: propComponents?.Header,
        List: (propComponents?.List ?? InfiniteListWrapper),
        TopItemList: (propComponents?.StickyItemList ?? InfiniteListWrapper),
        Item: (propComponents?.Item ?? InfiniteListItem),
        Footer: (propComponents?.Footer ??
            (loadMoreData !== undefined ? InfiniteListLoadingFooter : undefined)),
        EmptyPlaceholder: propComponents?.EmptyPlaceholder,
    }), [propComponents, loadMoreData]);
    const endReached = React.useCallback(() => loadMoreData?.(), [loadMoreData]);
    const combinedContext = React.useMemo(() => ({ ...context, gap }), [context, gap]);
    return (React.createElement(Virtuoso, { ...(overscan !== undefined ? { overscan } : {}), ...(initialItemCount !== undefined ? { initialItemCount } : {}), ...rest, topItemCount: stickyItemCount, ref: ref, useWindowScroll: useWindowScroll, customScrollParent: scrollParent ?? undefined, fixedItemHeight: fixedItemHeight, defaultItemHeight: estimatedItemHeight, className: className, data: data, context: combinedContext, endReached: loadMoreData !== undefined ? endReached : undefined, components: components, itemContent: renderItemContent, computeItemKey: itemKey }));
});

export { InfiniteList };
